import {
  BaseModel,
  Watchable,
  useInScopeModel,
  type ReadonlyEventWatchable,
  type ReadonlyWatchable,
} from '@cotera/client/v0/actions/framework';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { Assert } from '@cotera/utilities';
import { emitChatBotConfigUpdated } from '@cotera/client/app/etc/emit-chat-bot-config-updated';
import type { WatchablesFactory } from '@cotera/client/app/v1/watchables/watchables-factory';

export type LinearBotSettingsState = {
  loaded: boolean;
  installed: boolean;
  organizationId?: string;
  teams: Array<{
    teamId: string;
    teamName: string;
    agentId: string | null;
  }>;
  teamsError?: string;
};

const EMPTY_LINEAR_BOT_SETTINGS_STATE: LinearBotSettingsState = {
  loaded: false,
  installed: false,
  teams: [],
};

function linearBotSettingsSignature(state: LinearBotSettingsState): string {
  return `${state.loaded}|${state.installed}|${state.organizationId ?? ''}|${
    state.teamsError ?? ''
  }|${state.teams
    .map((team) => `${team.teamId}:${team.agentId ?? ''}`)
    .join(';')}`;
}

type LinearBotSettingsModelProps = {
  orgId: string;
  client: Pick<TenantedClient, 'toolsV2'>;
  watchablesFactory: Pick<
    WatchablesFactory,
    'makeChatBotConfigUpdatedWatchable'
  >;
};

export class LinearBotSettingsModel extends BaseModel {
  private readonly orgId: string;
  private readonly client: Pick<TenantedClient, 'toolsV2'>;
  private readonly _configWatchable: ReadonlyEventWatchable<LinearBotSettingsState>;
  private readonly _status: ReadonlyWatchable<'loading' | 'ready'>;
  private readonly _pendingTeamId = Watchable.fromValue<string | null>(null);

  constructor(props: LinearBotSettingsModelProps) {
    super('linear-bot-settings');
    this.orgId = props.orgId;
    this.client = props.client;

    this._configWatchable =
      props.watchablesFactory.makeChatBotConfigUpdatedWatchable<LinearBotSettingsState>(
        {
          runOnMount: true,
          initialValue: EMPTY_LINEAR_BOT_SETTINGS_STATE,
          equalityFn: (left, right) =>
            linearBotSettingsSignature(left) ===
            linearBotSettingsSignature(right),
          filter: (payload) =>
            payload?.orgId === this.orgId && payload.bot === 'linear',
          fn: async () => this.fetchState(),
        }
      );

    this._status = Watchable.from((get) => {
      return get(this._configWatchable).loaded ? 'ready' : 'loading';
    });
  }

  get config(): ReadonlyWatchable<LinearBotSettingsState> {
    return this._configWatchable;
  }

  get status(): ReadonlyWatchable<'loading' | 'ready'> {
    return this._status;
  }

  get pendingTeamId(): ReadonlyWatchable<string | null> {
    return this._pendingTeamId;
  }

  private async fetchState(): Promise<LinearBotSettingsState> {
    const installation = Assert.assertOk(
      await this.client.toolsV2.getLinearBotInstallation({})
    );

    if (installation.installed !== true) {
      return {
        loaded: true,
        installed: false,
        teams: [],
      };
    }

    const teamsResult = await this.client.toolsV2.listLinearTeamAgents({});
    if (teamsResult.isErr()) {
      const teamsError =
        teamsResult.error.t === 'application'
          ? teamsResult.error.err.message
          : 'Failed to load Linear teams';
      return {
        loaded: true,
        installed: true,
        organizationId: installation.organizationId,
        teams: [],
        teamsError,
      };
    }

    return {
      loaded: true,
      installed: true,
      organizationId: installation.organizationId,
      teams: teamsResult.value.teams,
    };
  }

  async refresh(): Promise<void> {
    await this._configWatchable.refresh();
  }

  async setTeamAgent(teamId: string, agentId: string): Promise<void> {
    this._pendingTeamId.set(teamId);
    try {
      Assert.assertOk(
        await this.client.toolsV2.setLinearTeamAgent({ teamId, agentId })
      );
      emitChatBotConfigUpdated(this.orgId, 'linear');
      await this._configWatchable.refresh();
    } finally {
      this._pendingTeamId.set(null);
    }
  }

  async clearTeamAgent(teamId: string): Promise<void> {
    this._pendingTeamId.set(teamId);
    try {
      Assert.assertOk(
        await this.client.toolsV2.clearLinearTeamAgent({ teamId })
      );
      emitChatBotConfigUpdated(this.orgId, 'linear');
      await this._configWatchable.refresh();
    } finally {
      this._pendingTeamId.set(null);
    }
  }

  dispose(): void {
    this._configWatchable.unsubscribe();
  }
}

export const useLinearBotSettingsModel = (): LinearBotSettingsModel => {
  const model = useInScopeModel(LinearBotSettingsModel);
  if (model === null) {
    throw new Error('LinearBotSettingsModel not found in scope');
  }
  return model;
};
